import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';

export default function AppLoginPage() {
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showMockNotice, setShowMockNotice] = useState(false);
  const [mockCode, setMockCode] = useState('');
  const [verifyMode, setVerifyMode] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  async function handleStart() {
    setError('');
    setSuccessMsg('');
    setShowMockNotice(false);
    setMockCode('');
    setIsRegistered(false);

    if (!phone || phone.length < 10) {
      setError('휴대폰 번호를 정확히 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      // 먼저 등록 여부만 확인 (login-direct, OTP 발송 없음)
      const { data, error: fnError } = await supabase.functions.invoke('login-direct', {
        body: { phone: phone.replace(/\D/g, '') },
      });

      if (fnError) {
        setLoading(false);
        setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        console.error('login-direct error:', fnError);
        return;
      }

      // 등록된 번호 + 인증 이력 있음 → 바로 로그인
      if (data && data.success && data.isRegistered) {
        setLoading(false);
        setIsRegistered(true);
        setSuccessMsg('이미 등록된 휴대폰 번호입니다.');

        // 등록된 사용자 바로 로그인 처리
        const rolesArray: ('patient' | 'caregiver')[] = data.roles || [];
        localStorage.setItem('kc_demo_login', JSON.stringify({
          phone: phone.replace(/\D/g, ''),
          userId: data.userId,
          roles: rolesArray,
          timestamp: Date.now(),
        }));
        localStorage.setItem('kc_demo_user_id', data.userId);

        // 잠시 메시지 보여준 후 홈으로 이동
        setTimeout(() => {
          if (rolesArray.length === 0) {
            navigate('/app/register', { state: { phone: phone.replace(/\D/g, '') } });
          } else {
            navigate('/app', { replace: true });
          }
        }, 1200);
        return;
      }

      // 등록된 번호지만 인증 이력 없음 → OTP 필요
      if (data && data.needOtp) {
        setLoading(false);
        // OTP 발송
        await sendOtpForLogin();
        return;
      }

      // 등록되지 않은 번호 → 회원가입 유도
      setLoading(false);
      setError('등록되지 않은 번호입니다. 아래 회원가입 버튼을 눌러 먼저 가입해주세요.');
    } catch (err) {
      setLoading(false);
      setError('네트워크 오류가 발생했습니다.');
      console.error(err);
    }
  }

  async function sendOtpForLogin() {
    setError('');
    setShowMockNotice(false);
    setMockCode('');
    setLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('send-otp-kr', {
        body: { phone: phone.replace(/\D/g, '') },
      });

      if (fnError) {
        setLoading(false);
        setError('인증번호 발송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        console.error('send-otp error:', fnError);
        return;
      }

      if (data && data.error) {
        setLoading(false);
        setError(data.error);
        return;
      }

      // 등록되지 않은 번호
      if (data && !data.isRegistered) {
        setLoading(false);
        setError('등록되지 않은 번호입니다. 아래 회원가입 버튼을 눌러 먼저 가입해주세요.');
        return;
      }

      setLoading(false);

      if (data && data.mock) {
        setShowMockNotice(true);
        if (data.code) {
          setMockCode(data.code);
        }
      }

      setVerifyMode(true);
      setCountdown(300);
    } catch (err) {
      setLoading(false);
      setError('네트워크 오류가 발생했습니다.');
      console.error(err);
    }
  }

  async function completeLogin(phoneNum: string, userId: string, roles: ('patient' | 'caregiver')[] | null) {
    const rolesArray = roles || [];
    localStorage.setItem('kc_demo_login', JSON.stringify({
      phone: phoneNum,
      userId: userId,
      roles: rolesArray,
      timestamp: Date.now(),
    }));
    localStorage.setItem('kc_demo_user_id', userId);

    if (rolesArray.length === 0) {
      navigate('/app/register', { state: { phone: phoneNum } });
    } else {
      navigate('/app', { replace: true });
    }
  }

  async function verifyOtp() {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('인증번호 6자리를 입력해주세요.');
      return;
    }
    setLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('verify-otp', {
        body: { phone: phone.replace(/\D/g, ''), code: otp },
      });

      setLoading(false);

      if (fnError) {
        setError('인증 확인 중 오류가 발생했습니다.');
        console.error('verify-otp error:', fnError);
        return;
      }

      if (data && data.error) {
        setError(data.error);
        return;
      }

      if (data && data.success) {
        await completeLogin(phone, data.userId, data.roles || []);
      } else {
        setError('인증에 실패했습니다.');
      }
    } catch (err) {
      setLoading(false);
      setError('네트워크 오류가 발생했습니다.');
      console.error(err);
    }
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-6 pt-12 pb-8 bg-white">
        {/* 헤더 */}
        <div className="mb-10">
          <button onClick={() => navigate('/app')} className="mb-4 text-gray-400 cursor-pointer">
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {verifyMode ? '인증번호 입력' : '휴대폰으로 시작하기'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {verifyMode
              ? `${phone}로 전송된 인증번호를 입력해주세요`
              : '휴대폰 번호를 입력하면 로그인 또는 회원가입을 진행합니다'
            }
          </p>
        </div>

        {/* 등록된 번호 성공 메시지 */}
        {successMsg && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-green-600 text-lg" />
            <div>
              <p className="text-sm font-semibold text-green-800">{successMsg}</p>
              <p className="text-xs text-green-600 mt-0.5">잠시 후 자동으로 이동합니다...</p>
            </div>
          </div>
        )}

        {/* 휴대폰 입력 */}
        {!verifyMode && (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">휴대폰 번호</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-medium px-3 py-3 bg-gray-100 rounded-xl shrink-0">+82</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 11)); setError(''); setSuccessMsg(''); }}
                  placeholder="01012345678"
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
                  autoComplete="off"
                />
              </div>
            </div>

            <button
              onClick={handleStart}
              disabled={loading || phone.length < 10}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />확인 중...
                </span>
              ) : (
                '시작하기'
              )}
            </button>
          </div>
        )}

        {/* OTP 입력 */}
        {verifyMode && (
          <div className="space-y-6">
            {showMockNotice && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-amber-600 mt-0.5" />
                  <div className="text-xs text-amber-800 leading-relaxed">
                    <p className="font-semibold mb-1">Solapi SMS 설정 안됨 (개발 모드)</p>
                    <p>실제 문자는 발송되지 않았습니다. 아래 인증번호를 입력하세요.</p>
                    {mockCode && (
                      <p className="mt-2 text-sm font-bold text-amber-900">개발용 인증번호: {mockCode}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">인증번호 6자리</label>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white text-center text-lg tracking-[0.5em] font-bold"
                autoComplete="off"
              />
            </div>

            <button
              onClick={verifyOtp}
              disabled={loading || otp.length !== 6}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />확인 중...
                </span>
              ) : (
                '인증하고 시작하기'
              )}
            </button>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-xs text-gray-400">{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')} 후에 다시 받을 수 있어요</p>
              ) : (
                <button
                  onClick={sendOtpForLogin}
                  disabled={loading}
                  className="text-xs text-[#2E7D6B] font-medium cursor-pointer"
                >
                  인증번호 다시 받기
                </button>
              )}
            </div>

            <button
              onClick={() => { setVerifyMode(false); setOtp(''); setError(''); setShowMockNotice(false); setMockCode(''); }}
              className="w-full text-center text-xs text-gray-400 cursor-pointer py-2"
            >
              휴대폰 번호 변경
            </button>
          </div>
        )}

        {/* 회원가입 유도 영역 (미등록 시 표시) */}
        {!verifyMode && error && error.includes('등록되지 않은 번호') && !isRegistered && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500 text-center mb-3">아직 회원이 아니신가요?</p>
            <button
              onClick={() => navigate('/app/register', { state: { phone } })}
              className="w-full py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-all border-2 border-[#2E7D6B] text-[#2E7D6B] bg-white"
            >
              회원가입하기
            </button>
          </div>
        )}

        {/* 일반 에러 메시지 */}
        {error && !error.includes('등록되지 않은 번호') && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">
            {error}
          </div>
        )}

        <p className="text-[10px] text-gray-400 text-center mt-6 leading-relaxed px-4">
          시작하기 버튼을 누르면 개인정보 수집 및 이용에 동의하는 것으로 간주됩니다.
        </p>
      </div>
    </AppLayout>
  );
}